import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { vocabularyApi } from '@/api/vocabulary'
import type { WordSource } from '@/types/common'
import { copy } from '@/lib/copy'
import {
  isPhraseInput,
  isValidEnglishPhrase,
  normalizeEnglishPhrase,
  sanitizeVocabularyInput,
} from '@/lib/phraseValidation'
import { isValidEnglishWord, normalizeEnglishWord } from '@/lib/wordValidation'

export interface AddedVocabularyItem {
  id: string
  surface: string
}

interface AddWordFormProps {
  onClose: () => void
  /** Called after a word/phrase is successfully added (before close). */
  onAdded?: (item: AddedVocabularyItem) => void
  source?: WordSource
  sourceReference?: string
}

function isValidInput(value: string): boolean {
  return isPhraseInput(value) ? isValidEnglishPhrase(value) : isValidEnglishWord(value)
}

function invalidFormatMessage(value: string): string {
  return isPhraseInput(value) ? copy.vocabulary.invalidPhraseFormat : copy.vocabulary.invalidWordFormat
}

export function AddWordForm({ onClose, onAdded, source = 'MANUAL', sourceReference }: AddWordFormProps) {
  const queryClient = useQueryClient()
  const [word, setWord] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async (): Promise<AddedVocabularyItem> => {
      if (isPhraseInput(word)) {
        const { data } = await vocabularyApi.addPhrase({
          normalizedPhrase: normalizeEnglishPhrase(word),
          source,
          sourceReference,
        })
        return { id: data.id, surface: data.surface }
      }
      const { data } = await vocabularyApi.addWord({
        word: normalizeEnglishWord(word),
        source,
        sourceReference,
      })
      return { id: data.id, surface: data.word }
    },
    onSuccess: async (added) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vocabulary'] }),
        queryClient.invalidateQueries({ queryKey: ['vocabularyPhrases'] }),
        queryClient.invalidateQueries({ queryKey: ['vocabularyStats'] }),
        queryClient.invalidateQueries({ queryKey: ['knownPhrases'] }),
      ])
      onAdded?.(added)
      onClose()
    },
    onError: (err: unknown) => {
      const response = (err as { response?: { status: number; data?: { message?: string } } })?.response
      const message = response?.data?.message
      if (response?.status === 409) {
        setError(
          message
          ?? (isPhraseInput(word) ? copy.vocabulary.phraseAlreadyExists : copy.vocabulary.wordAlreadyExists),
        )
        return
      }
      if (response?.status === 400 && message) {
        if (message.toLowerCase().includes('catalog phrase not found')) {
          setError(copy.vocabulary.phraseNotInCatalog)
          return
        }
        setError(message)
        return
      }
      setError(isPhraseInput(word) ? copy.vocabulary.addPhraseFailed : copy.vocabulary.addWordFailed)
    },
  })

  const canSubmit = isValidInput(word) && !mutation.isPending

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!isValidInput(word)) {
      setError(invalidFormatMessage(word))
      return
    }
    setError(null)
    mutation.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="add-word-form">
      <div className="w-full max-w-md rounded-xl bg-background p-6 shadow-2xl">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{copy.vocabulary.addWordTitle}</h2>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-1">
            <Label htmlFor="word">{copy.vocabulary.wordLabel}</Label>
            <Input
              id="word"
              autoComplete="off"
              autoFocus
              value={word}
              onChange={(e) => {
                setWord(sanitizeVocabularyInput(e.target.value))
                if (error) setError(null)
              }}
              placeholder={copy.vocabulary.wordPlaceholder}
            />
            <p className="text-xs text-muted-foreground">{copy.vocabulary.wordHint}</p>
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <div className="flex gap-3 pt-2">
            <Button type="button" className="flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/80" onClick={onClose}>
              {copy.actions.cancel}
            </Button>
            <Button
              type="submit"
              className="flex-1"
              loading={mutation.isPending}
              disabled={!canSubmit}
              data-testid="add-word-submit"
            >
              {copy.vocabulary.addWordSubmit}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
